import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { BlockMath, InlineMath } from 'react-katex';
import { ArrowLeft, ArrowRight, CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import SectionWrapper from '@/components/SectionWrapper';
import FormulaCard from '@/components/FormulaCard';
import SequenceNetworkBuilder from '@/calculators/SequenceNetworkBuilder';
import TheveninSeqCalc from '@/calculators/TheveninSeqCalc';
import SeqConnectionDiagram from '@/calculators/SeqConnectionDiagram';
import FaultCurrentPreviewCalc from '@/calculators/FaultCurrentPreviewCalc';

// ─── Color-coding legend ──────────────────────────────────────────────────────
const SequenceLegend = () => (
  <div
    data-testid="sequence-legend-m05"
    className="flex items-center gap-5 flex-wrap py-2 px-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-mono"
  >
    {[
      { color: '#0d9488', label: 'Positive (Z\u2081)', sub: 'ABC rotation \u2014 source V\u2080 drives current' },
      { color: '#ea580c', label: 'Negative (Z\u2082)', sub: 'ACB rotation \u2014 no source, Z\u2082\u2248Z\u2081' },
      { color: '#7c3aed', label: 'Zero (Z\u2080)',     sub: 'Cophasal \u2014 topology set by grounding' },
    ].map(({ color, label, sub }) => (
      <span key={label} className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-full flex-shrink-0" style={{ background: color }} />
        <span className="font-semibold" style={{ color }}>{label}</span>
        <span className="text-slate-400 dark:text-slate-500">— {sub}</span>
      </span>
    ))}
  </div>
);

// ─── Section 1: Why It Matters ────────────────────────────────────────────────
const WhyItMatters = () => (
  <SectionWrapper number="1" title="Why Sequence Networks Matter" id="why-it-matters">
    <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-5 mb-5">
      <p className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">Real Utility Scenario</p>
      <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm">
        A relay engineer sets a 51G ground overcurrent relay on a 13.8 kV Dyn11 distribution feeder.
        Using only positive-sequence analysis, she estimates a worst-case SLG fault current of{' '}
        <InlineMath math="I_F = V_f / Z_1 = 1.0 / 0.15 = 6.67\,\text{pu}" />. She sets the relay pickup
        at 20% of that value. During commissioning, a bolted SLG test fault produces only 3.85 pu —
        and the relay fails to pick up. The problem: the engineer used only <InlineMath math="Z_1" /> when
        the actual SLG fault current is{' '}
        <InlineMath math="3V_f / (Z_1 + Z_2 + Z_0)" />. For this Dyn11 feeder, the HV delta winding
        eliminates <InlineMath math="Z_{0s}" /> from the zero-sequence path, making{' '}
        <InlineMath math="Z_0 = Z_T + Z_{0L} = 0.26\,\text{pu}" />, far higher than{' '}
        <InlineMath math="Z_1 = 0.15\,\text{pu}" />. Without sequence networks, the grounding topology
        is invisible.
      </p>
    </div>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
      In M04 you learned to <em>decompose</em> any unbalanced fault into three balanced sequence sets.
      Module 05 answers the next question:{' '}
      <strong className="text-slate-800 dark:text-slate-200">
        what is the Thevenin equivalent circuit of the power system at the fault bus, expressed
        separately for each sequence domain?
      </strong>{' '}
      Each sequence network is a standalone circuit — a single voltage source and a single Thevenin
      impedance — that applies exclusively within its sequence domain. The three networks are completely
      decoupled under normal balanced conditions, and are connected only at the fault point by the
      fault boundary conditions.
    </p>
    <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
      The zero-sequence network topology is the most critical: unlike Z\u2081 and Z\u2082 which follow
      the physical impedance path, Z\u2080 depends entirely on the transformer winding connections and
      neutral grounding arrangement. A single delta winding can completely open the zero-sequence
      circuit — blocking all ground fault current. Understanding how to build and read sequence
      networks is the essential prerequisite for every fault study, relay coordination analysis, and
      equipment duty calculation.
    </p>
  </SectionWrapper>
);

// ─── Section 2: Sequence Network Builder (Interactive First) ─────────────────
const NetworkBuilderSection = () => (
  <SectionWrapper number="2" title="Sequence Network Builder" id="network-builder" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
      Select a transformer connection and system grounding type. The three sequence networks update
      instantly — teal for positive, orange for negative, purple for zero. The delta block symbol in
      the zero-sequence diagram shows where the circuit is broken. Read the plain-English callout below
      the diagrams for a complete explanation of what changes and why.
    </p>
    <SequenceLegend />
    <div className="mt-5">
      <SequenceNetworkBuilder />
    </div>
  </SectionWrapper>
);

// ─── Section 3: Core Theory ───────────────────────────────────────────────────
const THEORY_ITEMS = [
  {
    id: 'th1',
    title: 'What is a Sequence Network? — The Thevenin Equivalent Per Domain',
    body: "A sequence network is the Thevenin equivalent of the entire power system as seen from the fault bus, but evaluated within only one sequence domain at a time. For each sequence (positive, negative, zero), every generator, transformer, transmission line, and load in the system is replaced by its sequence-domain impedance, connected in a series-parallel network from the fault bus back to a reference node. The network is then reduced to a single Thevenin impedance (Z1_Th, Z2_Th, Z0_Th) and — for the positive sequence only — a single Thevenin voltage source (Vf = pre-fault bus voltage, typically 1.0 pu).\n\nThe three resulting Thevenin equivalents are independent circuits. Under normal balanced operation they are completely separate — no current flows between them. They are only connected together at the fault bus when a fault occurs, and the type of fault determines how they are interconnected (series for SLG, series Z1+Z2 for LL, etc.).",
  },
  {
    id: 'th2',
    title: 'Building the Positive and Negative Sequence Networks',
    body: "Positive-sequence network (teal, Z\u2081): Contains all three-phase balanced system components — generators (with their positive-sequence internal impedance), transformers (series leakage impedance), transmission lines (positive-sequence line impedance Z1_line), and loads (usually omitted in fault studies because fault impedance >> load impedance). The Thevenin source voltage Vf is the pre-fault voltage at the fault bus (typically 1.0 pu). The Thevenin impedance Z1_Th is found by shorting Vf and measuring the impedance from the fault bus.\n\nNegative-sequence network (orange, Z\u2082): Structurally identical to the positive-sequence network, but with no voltage source (negative-sequence voltages are generated only by unbalance or asymmetry, not by the rotating machine fundamental frequency). For static elements (transformers, lines, cables), Z\u2082 = Z\u2081 exactly. For generators and synchronous motors, Z\u2082 \u2260 Z\u2081 (Z\u2082 is typically 0.1\u20130.2 pu vs Z\u2081 = 0.05\u20130.10 pu for the d-axis subtransient reactance). For distribution feeders far from large generators, Z\u2082 \u2248 Z\u2081 is a valid simplification.",
  },
  {
    id: 'th3',
    title: 'Building the Zero-Sequence Network — Topology Rules by Transformer Connection',
    body: "Zero-sequence network (purple, Z\u2080): The most complex of the three because its topology depends directly on transformer winding connections and neutral grounding methods. The rules:\n\n\u2022 Grounded-Wye winding: provides a zero-sequence return path. Appears as impedance (ZT for transformers) in the zero-sequence circuit. If the neutral has a grounding resistor Rn, it appears as 3Rn (not Rn) in the zero-sequence circuit, because zero-sequence current I\u2080 flows simultaneously in all three phases, and I_neutral = 3I\u2080.\n\n\u2022 Delta winding: has no neutral point — zero-sequence current cannot enter or exit the delta in the line conductors. The delta side appears as an open circuit in the zero-sequence network of the external system. (Zero-sequence current does circulate inside the delta winding loop, but this is an internal circulating current that has no effect on the external network.)\n\n\u2022 Ungrounded Wye: floating neutral provides no zero-sequence path. Appears as an open circuit in the zero-sequence network, regardless of the phase-to-neutral voltage.\n\nThe consequence: for Dyn11 (most common North American distribution transformer), the delta HV winding eliminates Z\u2080_source from the zero-sequence path. The zero-sequence Thevenin seen from the LV fault bus is Z_T + Z\u2080_line only. For YNyn0 (both neutrals grounded), Z\u2080_source + Z_T + Z\u2080_line all appear in series. For Dd0 (both delta), no zero-sequence current can flow anywhere in the external circuit on either side.",
  },
];

const CoreTheory = () => (
  <SectionWrapper number="3" title="Core Theory" id="core-theory">
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
      Sequence network theory rests on three pillars: the Thevenin representation of each domain,
      the construction rules for positive and negative networks, and the transformer-connection
      topology rules that govern the zero-sequence network.
    </p>
    <Accordion type="single" collapsible defaultValue="th1" className="space-y-2" data-testid="theory-accordion">
      {THEORY_ITEMS.map((item) => (
        <AccordionItem
          key={item.id}
          value={item.id}
          className="border border-slate-200 dark:border-slate-700 rounded-lg px-4 bg-white dark:bg-slate-900"
        >
          <AccordionTrigger className="text-sm font-medium text-slate-800 dark:text-slate-200 hover:no-underline hover:text-teal-700 dark:hover:text-teal-400 py-3">
            <span className="flex items-center gap-2.5 text-left">
              <span className="text-teal-600 dark:text-teal-500 font-mono font-bold text-xs w-4 flex-shrink-0">
                {item.id.replace('th', '')}
              </span>
              {item.title}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pb-4 space-y-3">
              {item.body.split('\n\n').map((para, pi) => (
                <p key={pi} className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed whitespace-pre-line">
                  {para}
                </p>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </SectionWrapper>
);

// ─── Section 4: Formula Sheet ─────────────────────────────────────────────────
const FormulaSheet = () => (
  <SectionWrapper number="4" title="Formula Sheet" id="formulas" alt>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <FormulaCard
        title="Thevenin Positive-Sequence Impedance (IEEE Std 1110)"
        formula={"Z_1^{Th} = Z_{1s} + Z_T + Z_{1L}"}
        variables={[
          { sym: 'Z\u2081\u1d54\u02b0', desc: 'Thevenin positive-sequence impedance at fault bus (pu)' },
          { sym: 'Z\u2081s',  desc: 'Source positive-sequence Thevenin impedance (pu) \u2014 utility or generator subtransient reactance' },
          { sym: 'Z_T',   desc: 'Transformer leakage impedance (pu on system base) \u2014 from nameplate %Z' },
          { sym: 'Z\u2081L',  desc: 'Feeder line positive-sequence impedance from transformer bus to fault (pu)' },
        ]}
        note="Assumption: simplified series model; shunt admittances neglected (valid for distribution fault studies). Numerical example: Z\u2081s = 0.10 pu, Z_T = 0.08 pu, Z\u2081L = 0.05 pu \u2192 Z\u2081_Th = 0.23 pu. Negative-sequence: Z\u2082_Th \u2248 Z\u2081_Th for static systems (transformers, lines, cables). For Z\u2082 \u2260 Z\u2081, see rotating machine datasheets."
      />
      <FormulaCard
        title="Zero-Sequence Thevenin — By Transformer Connection"
        formula={"\\begin{aligned} \\text{Dyn11 (gnd LV):}\\quad & Z_0^{Th} = Z_T + Z_{0L} \\\\ \\text{YNyn0 (gnd):}\\quad & Z_0^{Th} = Z_{0s} + Z_T + Z_{0L} \\\\ \\text{Dd0 (any):}\\quad & Z_0^{Th} = \\infty\\ (\\text{open}) \\end{aligned}"}
        variables={[
          { sym: 'Z\u2080s',  desc: 'Source zero-sequence Thevenin impedance (pu) \u2014 blocked by HV delta in Dyn11' },
          { sym: 'Z_T',  desc: 'Transformer impedance (pu) \u2014 appears in Z\u2080 if a wye winding provides a ground path' },
          { sym: 'Z\u2080L',  desc: 'Zero-sequence feeder line impedance (pu) \u2014 for overhead lines, Z\u2080L \u2248 3\u00d7Z\u2081L' },
          { sym: '\u221e',    desc: 'Open circuit \u2014 no zero-sequence current can flow. SLG fault current = 0.' },
        ]}
        note="Numerical example (Dyn11, solidly gnd): Z_T = 0.08 pu, Z\u2080L = 0.18 pu \u2192 Z\u2080_Th = 0.26 pu. Compare to Z\u2081_Th = 0.23 pu \u2014 Z\u2080 > Z\u2081 for overhead feeder, so SLG fault current < 3PH fault current."
      />
      <FormulaCard
        title="SLG Fault — Series Network Connection (IEEE Std C37.010)"
        formula={"I_1 = I_2 = I_0 = \\frac{V_f}{Z_1^{Th} + Z_2^{Th} + Z_0^{Th}} \\quad I_F = 3I_1"}
        variables={[
          { sym: 'I\u2081, I\u2082, I\u2080', desc: 'Sequence fault currents (pu) \u2014 equal magnitudes are the SLG fingerprint' },
          { sym: 'V_f',  desc: 'Pre-fault voltage at fault bus (pu) \u2014 standard assumption: V_f = 1.0 pu' },
          { sym: 'I_F',  desc: 'Phase A fault current (pu) = 3I\u2081 \u2014 because I_a = I\u2080 + I\u2081 + I\u2082 = 3I\u2081 for SLG' },
        ]}
        note="Assumption: Z\u2082 \u2248 Z\u2081 for distribution. Numerical example: Vf = 1.0 pu, Z\u2081_Th = Z\u2082_Th = 0.23 pu, Z\u2080_Th = 0.26 pu \u2192 I\u2081 = 1.0/(0.23+0.23+0.26) = 1.389 pu, I_F = 3\u00d71.389 = 4.17 pu. Compare to 3PH: Vf/Z\u2081 = 1.0/0.23 = 4.35 pu."
      />
      <FormulaCard
        title="Neutral Resistor in Zero-Sequence (3Rn Rule)"
        formula={"Z_{0,\\text{neutral}} = 3R_n \\qquad I_{\\text{neutral}} = 3I_0"}
        variables={[
          { sym: 'R_n',  desc: 'Physical grounding resistor value (\u03a9 or pu) connected between neutral and ground' },
          { sym: '3R_n', desc: 'Impedance as it appears in the zero-sequence circuit \u2014 three times the physical value' },
          { sym: 'I\u2080', desc: 'Zero-sequence current component (pu) \u2014 flows equally in all three phases' },
          { sym: 'I_neutral', desc: 'Neutral conductor current (pu) = 3I\u2080, because all three phase currents return via neutral' },
        ]}
        note="Critical insight: zero-sequence current flows simultaneously in all three line conductors in the same direction. The return current in the neutral is the sum of all three phase currents, equal to 3I\u2080. This is why Rn in the physical circuit appears as 3Rn in the zero-sequence network. Numerical: Rn = 0.05 pu \u2192 3Rn = 0.15 pu (adds directly to Z\u2080_Th in series)."
      />
    </div>
  </SectionWrapper>
);

// ─── Section 5: Common Mistakes ────────────────────────────────────────────────
const MISTAKES = [
  {
    title: 'Forgetting that a delta winding opens the zero-sequence circuit',
    body: 'The most consequential error in zero-sequence network construction. When a transformer has a delta winding on either side, that delta acts as an open circuit in the zero-sequence network for the external line conductors on that side. This is not a modeling approximation — it is an exact statement of Kirchhoff\'s current law for zero-sequence: there is no neutral point for current to enter or exit, so the zero-sequence line current on the delta side is identically zero. \n\nFor Dyn11 (HV delta, LV grounded wye), the HV source-side impedance Z\u2080s is completely disconnected from the zero-sequence network. The engineer who writes Z\u2080_Th = Z\u2080s + Z_T + Z\u2080_line for a Dyn11 system will overestimate SLG fault current by including source impedances that physically cannot contribute.',
  },
  {
    title: 'Assuming Z\u2080_line = Z\u2081_line for overhead feeders',
    body: 'For overhead transmission and distribution lines with full static wire / neutral conductors, the zero-sequence line impedance Z\u2080_line is typically 3\u00d7 Z\u2081_line, not equal to it. The higher Z\u2080_line arises because zero-sequence current returns through the earth, which has much higher effective resistance and a much larger current loop than the positive-sequence path between the phase conductors.\n\nFor underground cables, Z\u2080_line \u2248 Z\u2081_line because the concentric neutral or metallic sheath provides a low-impedance return path close to the phase conductor. Using Z\u2080 = Z\u2081 for an overhead feeder will significantly underestimate Z\u2080_Th and overestimate SLG fault current. The typical default for overhead feeders: Z\u2080_line = 3\u00d7Z\u2081_line.',
  },
  {
    title: 'Using R_n instead of 3R_n for resistance-grounded neutrals',
    body: 'When a wye-connected winding has a grounding resistor R_n between the neutral and ground, the resistor appears as 3R_n in the zero-sequence circuit, not as R_n. The factor-of-three comes from the fact that zero-sequence current flows simultaneously in all three phase conductors. The neutral current I_neutral = I_a + I_b + I_c = 3I\u2080, so the voltage drop across R_n is I_neutral \u00d7 R_n = 3I\u2080 \u00d7 R_n = I\u2080 \u00d7 (3R_n).\n\nUsing R_n instead of 3R_n in the zero-sequence network will overestimate SLG fault current by up to 3\u00d7 for high-resistance grounded systems. In OHGR systems designed to limit fault current to 1\u20135 A, this error can make the calculation show hundreds of amps instead of single digits.',
  },
  {
    title: 'Using different per-unit bases for different sequence networks',
    body: 'All three sequence networks \u2014 positive, negative, and zero \u2014 must use exactly the same MVA base and the same kV base on each voltage level. Sequence impedances are often given on equipment-specific bases (transformer nameplate MVA, generator rated MVA) and must be converted to the system base before being placed in any sequence network. The sequence domain does not change the per-unit conversion formula.\n\nA common error: the zero-sequence data for cables or grounding transformers is found in a different document at a different base. If the base conversion is missed, Z\u2080 may be 10\u00d7 too large or too small. Always verify: what MVA base and kV base is each impedance value referenced to, before placing it in any sequence network.',
  },
  {
    title: 'Assuming Z\u2081 = Z\u2082 for all equipment, including generators and motors',
    body: 'Z\u2082 \u2248 Z\u2081 is only valid for static equipment: transformers, transmission lines, cables, and passive components. For rotating machines (synchronous generators, synchronous motors, induction motors), Z\u2082 is significantly different from Z\u2081 and must be taken from the equipment nameplate or datasheet.\n\nFor synchronous generators, Z\u2082 is typically 1.5\u20132\u00d7 Z\u2081 (negative-sequence impedance is higher than subtransient reactance). For induction motors, Z\u2082 can be as low as Z\u2081 / 5. Using Z\u2081 in place of Z\u2082 for rotating machines will produce incorrect SLG and LL fault current results, and will give wrong negative-sequence relay (46) and negative-sequence protection settings. For distribution feeders without large in-line generators, Z\u2082 \u2248 Z\u2081 is acceptable. For systems with distributed generation or large motors, always verify the Z\u2082 source.',
  },
];

const CommonMistakes = () => (
  <SectionWrapper number="5" title="Common Mistakes" id="common-mistakes">
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
      Sequence network errors cluster into three categories: topology errors (delta blocking, neutral
      paths), impedance value errors (Z\u2080_line, 3Rn factor, Z\u2081 vs Z\u2082), and base errors
      (mixing per-unit bases across sequences). Identify the category first, then trace the fault path.
    </p>
    <Accordion type="single" collapsible className="space-y-2" data-testid="mistakes-accordion">
      {MISTAKES.map((m, i) => (
        <AccordionItem
          key={i}
          value={`mistake-${i}`}
          className="border border-slate-200 dark:border-slate-700 rounded-lg px-4 bg-white dark:bg-slate-900"
        >
          <AccordionTrigger className="text-sm font-medium text-slate-800 dark:text-slate-200 hover:no-underline hover:text-teal-700 dark:hover:text-teal-400 py-3">
            <span className="flex items-center gap-2.5 text-left">
              <span className="text-red-500 text-xs font-bold w-5 flex-shrink-0">#{i + 1}</span>
              {m.title}
            </span>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pb-4 space-y-3">
              {m.body.split('\n\n').map((para, pi) => (
                <p key={pi} className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{para}</p>
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  </SectionWrapper>
);

// ─── Section 6: Worked Example ────────────────────────────────────────────────
const STEPS = [
  {
    label: 'State the system parameters',
    text: 'System: Dyn11 transformer (HV delta, LV solidly-grounded wye), 10 MVA, 69/13.8 kV. Base: 10 MVA, 13.8 kV on LV. Pre-fault voltage at LV bus: Vf = 1.0 pu. Fault type: bolted SLG on Phase A at end of feeder. Given impedances (all on system base, pu): Z\u2081s = 0.10\u228080\u00b0, Z_T = 0.08\u228085\u00b0, Z\u2081_line = 0.05\u228072\u00b0, Z\u2080_line = 0.18\u228068\u00b0.',
    formula: null,
    result: null,
  },
  {
    label: 'Build Z\u2081_Th \u2014 Positive-Sequence Thevenin Impedance',
    text: 'Series combination of source, transformer, and line impedances. Using magnitudes only for this educational example (full phasor calculation follows in the calculator below).',
    formula: "Z_1^{Th} = Z_{1s} + Z_T + Z_{1L} \\approx 0.10 + 0.08 + 0.05 = 0.23\\,\\text{pu}",
    result: 'Z\u2081_Th = 0.23 pu',
  },
  {
    label: 'Set Z\u2082_Th \u2014 Negative-Sequence (Static System)',
    text: 'This feeder has no in-line generators or large motors. All elements are static (transformer + overhead line). For static equipment Z\u2082 = Z\u2081 exactly. Therefore Z\u2082_Th = Z\u2081_Th.',
    formula: "Z_2^{Th} \\approx Z_1^{Th} = 0.23\\,\\text{pu} \\quad (\\text{static elements only})",
    result: 'Z\u2082_Th = 0.23 pu (same as Z\u2081_Th — no rotating machines)',
  },
  {
    label: 'Build Z\u2080_Th \u2014 Zero-Sequence for Dyn11 Solidly Grounded',
    text: 'Dyn11: the HV delta winding completely blocks Z\u2080s from the zero-sequence path. Only Z_T and Z\u2080_line contribute. Z\u2080_line \u2248 3\u00d7Z\u2081_line for this overhead feeder (0.18 vs 0.05 pu confirms the 3\u00d7 relationship).',
    formula: "Z_0^{Th}\\big|_{Dyn11} = Z_T + Z_{0L} = 0.08 + 0.18 = 0.26\\,\\text{pu} \\qquad (Z_{0s}\\text{ blocked by HV }\u0394)",
    result: 'Z\u2080_Th = 0.26 pu (Z\u2080s excluded — HV delta blocks source contribution)',
  },
  {
    label: 'Connect Networks for SLG \u2014 Series Connection',
    text: 'For a single-line-to-ground fault on Phase A: boundary conditions are Va = 0, Ib = Ic = 0. This forces I\u2081 = I\u2082 = I\u2080 (equal sequence currents) and requires Z\u2081, Z\u2082, Z\u2080 in series at the fault bus. Calculate sequence current and total fault current.',
    formula: "I_1 = I_2 = I_0 = \\frac{V_f}{Z_1^{Th}+Z_2^{Th}+Z_0^{Th}} = \\frac{1.0}{0.23+0.23+0.26} = \\frac{1.0}{0.72} = 1.389\\,\\text{pu}",
    result: 'I\u2081 = I\u2082 = I\u2080 = 1.389 pu (equal magnitudes confirm SLG signature)',
  },
  {
    label: 'Calculate Fault Current and Compare to Three-Phase',
    text: 'Phase A fault current = 3I\u2081 (since all three sequence components add in phase on Phase A for SLG). Compare to three-phase bolted fault for context.',
    formula: "I_F = 3I_1 = 3 \\times 1.389 = 4.17\\,\\text{pu} \\quad\\text{vs}\\quad I_{F,3\\phi} = \\frac{V_f}{Z_1^{Th}} = \\frac{1.0}{0.23} = 4.35\\,\\text{pu}",
    result: 'SLG fault (4.17 pu) < 3PH fault (4.35 pu) \u2014 overhead feeder Z\u2080 > Z\u2081 limits ground current',
  },
];

const WorkedExample = () => (
  <SectionWrapper number="6" title="Step-by-Step Worked Example" id="worked-example">
    <div className="mb-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5">
      <p className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-widest mb-2">
        Problem Statement
      </p>
      <p className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
        A 10 MVA, 69/13.8 kV{' '}
        <strong className="font-mono text-slate-900 dark:text-white">Dyn11</strong> distribution
        transformer supplies a 13.8 kV overhead feeder. The LV neutral is solidly grounded. A bolted
        single-line-to-ground fault occurs on Phase A at the far end of the feeder. Using the
        sequence network method, find the fault current and explain why SLG differs from three-phase
        for this system.
      </p>
    </div>
    <div className="space-y-3" data-testid="worked-example-steps">
      {STEPS.map((step, i) => (
        <div
          key={i}
          className="border border-slate-200 dark:border-slate-700 rounded-lg p-4 bg-white dark:bg-slate-900"
        >
          <div className="flex items-start gap-3">
            <div className="section-badge flex-shrink-0 text-xs">{i + 1}</div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 dark:text-white text-sm mb-2 font-heading">
                {step.label}
              </p>
              {step.text && (
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed mb-2">
                  {step.text}
                </p>
              )}
              {step.formula && (
                <div className="overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3 my-2">
                  <BlockMath math={step.formula} />
                </div>
              )}
              {step.result && (
                <div className="inline-flex items-center gap-2 bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-700 rounded-md px-3 py-1.5 mt-1">
                  <ChevronRight className="h-3 w-3 text-teal-600" />
                  <span className="text-xs font-mono font-semibold text-teal-800 dark:text-teal-300">
                    {step.result}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
    <div className="mt-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
      <p className="text-xs font-semibold text-amber-700 dark:text-amber-400 mb-1">Key result to remember</p>
      <p className="text-sm text-slate-700 dark:text-slate-300 leading-relaxed">
        For overhead feeders with Dyn11 transformers, SLG fault current is <em>lower</em> than
        three-phase fault current because Z\u2080_line \u2248 3\u00d7Z\u2081_line adds significant impedance
        in series with the ground fault path. For cables (Z\u2080 \u2248 Z\u2081) or systems with large
        Z\u2080s from the source, the SLG current can{' '}
        <em>exceed</em> three-phase current — this is why relay engineers always check both cases.
      </p>
    </div>
  </SectionWrapper>
);

// ─── Section 7: Interactive Calculators ───────────────────────────────────────
const CalculatorsSection = () => (
  <SectionWrapper number="7" title="Interactive Calculators" id="calculators" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-4">
      Three calculators in learning order: build the Thevenin impedances first, then study how the
      sequence networks connect for each fault type, then compute actual fault currents with
      adjustable sliders.
    </p>
    <SequenceLegend />
    <div className="mt-5">
      <Tabs defaultValue="thevenin" data-testid="calculators-tabs">
        <TabsList className="flex flex-wrap gap-1 h-auto mb-6 bg-slate-100 dark:bg-slate-800/60 p-1 rounded-lg">
          <TabsTrigger
            value="thevenin"
            data-testid="tab-thevenin"
            className="text-xs font-medium px-3 py-2 rounded-md flex-shrink-0"
          >
            1. Thevenin Impedances
          </TabsTrigger>
          <TabsTrigger
            value="connection"
            data-testid="tab-connection"
            className="text-xs font-medium px-3 py-2 rounded-md flex-shrink-0"
          >
            2. Network Connection
          </TabsTrigger>
          <TabsTrigger
            value="fault-preview"
            data-testid="tab-fault-preview"
            className="text-xs font-medium px-3 py-2 rounded-md flex-shrink-0"
          >
            3. Fault Current Preview
          </TabsTrigger>
        </TabsList>

        <TabsContent value="thevenin">
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 bg-white dark:bg-slate-900">
            <div className="mb-4">
              <p className="font-semibold text-slate-900 dark:text-white text-sm font-heading mb-1">
                Thevenin Sequence Impedance Calculator
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Enter source, transformer, and line impedances (magnitude and angle) for each sequence.
                The calculator builds Z\u2081_Th, Z\u2082_Th, and Z\u2080_Th. Use the results as inputs to the
                Fault Current Preview calculator in Tab 3.
              </p>
            </div>
            <TheveninSeqCalc />
          </div>
        </TabsContent>

        <TabsContent value="connection">
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 bg-white dark:bg-slate-900">
            <div className="mb-4">
              <p className="font-semibold text-slate-900 dark:text-white text-sm font-heading mb-1">
                Fault Network Connection Diagram
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Select a fault type to see exactly how Z\u2081, Z\u2082, Z\u2080 are interconnected at the fault
                bus. Shows the boundary conditions, sequence current formula, and which sequence
                networks carry current for each fault type.
              </p>
            </div>
            <SeqConnectionDiagram />
          </div>
        </TabsContent>

        <TabsContent value="fault-preview">
          <div className="border border-slate-200 dark:border-slate-700 rounded-xl p-5 bg-white dark:bg-slate-900">
            <div className="mb-4">
              <p className="font-semibold text-slate-900 dark:text-white text-sm font-heading mb-1">
                Fault Current Preview Calculator
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Enter the Thevenin sequence impedances (from Tab 1) and adjust Vf. Select any fault
                type and see the sequence currents and total fault current computed instantly, with a
                color-coded bar chart showing relative contributions.
              </p>
            </div>
            <FaultCurrentPreviewCalc />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  </SectionWrapper>
);

// ─── Section 8: Self-Check Quiz ───────────────────────────────────────────────
const QUESTIONS = [
  {
    q: 'For a Dyn11 transformer (HV delta, LV solidly-grounded wye), the zero-sequence Thevenin impedance seen from the LV fault bus is:',
    options: [
      { k: 'a', label: 'Z\u2080s + Z_T + Z\u2080_line  (full series path from source to fault)' },
      { k: 'b', label: 'Z_T + Z\u2080_line  (source side blocked by HV delta)' },
      { k: 'c', label: 'Z\u2080_line only  (transformer not in zero-sequence path)' },
      { k: 'd', label: 'Open circuit  (no zero-sequence path exists)' },
    ],
    correct: 'b',
    explanation: 'Z\u2080_Th = Z_T + Z\u2080_line. The HV delta winding has no neutral point, so zero-sequence current cannot enter the HV line conductors from the source side. Z\u2080s is completely excluded. Only the transformer impedance (providing the zero-sequence return through the grounded LV neutral) and the feeder zero-sequence line impedance remain in the path. If the fault is at the LV bus terminals directly, Z\u2080_line = 0 and Z\u2080_Th = Z_T only.',
  },
  {
    q: 'For an overhead distribution feeder with earth return, the zero-sequence line impedance Z\u2080_line relates to the positive-sequence impedance Z\u2081_line approximately as:',
    options: [
      { k: 'a', label: 'Z\u2080_line \u2248 Z\u2081_line  (same path, same impedance)' },
      { k: 'b', label: 'Z\u2080_line \u2248 2\u00d7Z\u2081_line' },
      { k: 'c', label: 'Z\u2080_line \u2248 3\u00d7Z\u2081_line' },
      { k: 'd', label: 'Z\u2080_line \u2248 Z\u2081_line / 3  (zero-sequence uses all three conductors in parallel)' },
    ],
    correct: 'c',
    explanation: 'Z\u2080_line \u2248 3\u00d7Z\u2081_line for overhead feeders with earth return. The factor of 3 arises because zero-sequence current returns through the earth, which has much higher resistance and a much larger loop area than the phase-conductor return of positive-sequence. For underground cables with concentric neutrals, Z\u2080 \u2248 Z\u2081 because the close-proximity shield provides a low-impedance return. Always check the feeder type before applying the 3\u00d7 rule.',
  },
  {
    q: 'For a Delta-Delta (Dd0) transformer, the zero-sequence Thevenin impedance seen from either terminal is:',
    options: [
      { k: 'a', label: 'Z_T  (transformer impedance only)' },
      { k: 'b', label: 'Z\u2080s + Z_T  (source and transformer in series)' },
      { k: 'c', label: 'Open circuit \u2014 no zero-sequence current can flow in the external line conductors' },
      { k: 'd', label: 'Short circuit \u2014 zero-sequence circulates freely inside the delta loops' },
    ],
    correct: 'c',
    explanation: 'Dd0: both HV and LV windings are delta-connected. Neither side has a neutral point. Zero-sequence current cannot enter or exit the delta in the external line conductors on either side, regardless of any external grounding. The zero-sequence network is permanently open on both sides of the transformer. (Zero-sequence current does circulate internally inside each delta loop, but this internal circulating current does not affect the external sequence network.) SLG fault current through this transformer is zero.',
  },
  {
    q: 'For a single-line-to-ground (SLG) fault, how are the three Thevenin sequence networks connected at the fault bus?',
    options: [
      { k: 'a', label: 'Z\u2081 in parallel with (Z\u2082 + Z\u2080)' },
      { k: 'b', label: 'Z\u2081, Z\u2082, Z\u2080 all in series' },
      { k: 'c', label: 'Z\u2081 only — same as three-phase fault' },
      { k: 'd', label: 'Z\u2082 in parallel with Z\u2080, then in series with Z\u2081' },
    ],
    correct: 'b',
    explanation: 'SLG fault: series connection. The boundary conditions for a phase-A SLG fault are Va = 0, Ib = Ic = 0. Applying these to the symmetrical components transformation gives I\u2081 = I\u2082 = I\u2080, which forces the three sequence networks to appear in series at the fault bus. The total Thevenin impedance for SLG is Z\u2081 + Z\u2082 + Z\u2080. The parallel combination (Z\u2082\u2225Z\u2080 + Z\u2081) applies to double-line-to-ground (DLG) faults instead.',
  },
  {
    q: 'A wye-connected neutral has a grounding resistor of Rn = 0.05 pu. How does this appear in the zero-sequence circuit?',
    options: [
      { k: 'a', label: '0.05 pu  (Rn as-is)' },
      { k: 'b', label: '0.10 pu  (2\u00d7Rn)' },
      { k: 'c', label: '0.15 pu  (3\u00d7Rn)' },
      { k: 'd', label: '0.0167 pu  (Rn/3)' },
    ],
    correct: 'c',
    explanation: '3\u00d7Rn = 3 \u00d7 0.05 = 0.15 pu. Zero-sequence current I\u2080 flows simultaneously in all three phase conductors in the same direction. The neutral current is I_neutral = I_a + I_b + I_c = 3I\u2080. The voltage drop across Rn = I_neutral \u00d7 Rn = 3I\u2080 \u00d7 Rn. In the zero-sequence circuit where the loop current is I\u2080, this voltage drop appears as a series impedance of 3Rn. Using Rn instead of 3Rn is one of the most common calculation errors in resistance-grounded system design.',
  },
];

const SelfCheckQuiz = () => {
  const [selected, setSelected] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const score = submitted ? QUESTIONS.filter((q, i) => selected[i] === q.correct).length : 0;
  const reset = () => { setSelected({}); setSubmitted(false); };

  return (
    <SectionWrapper number="8" title="Self-Check Quiz" id="quiz">
      <p className="text-slate-600 dark:text-slate-400 text-sm mb-6">
        Five questions on sequence network topology: zero-sequence path rules for Dyn11 and Dd0
        transformers, overhead-vs-cable Z\u2080 ratios, SLG series connection, and the 3Rn neutral
        resistor rule.
      </p>
      <div className="space-y-6" data-testid="quiz-section">
        {QUESTIONS.map((q, qi) => (
          <div
            key={qi}
            data-testid={`quiz-question-${qi}`}
            className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5"
          >
            <p className="font-medium text-slate-900 dark:text-white text-sm mb-3">
              <span className="text-teal-700 dark:text-teal-400 font-mono mr-2">Q{qi + 1}.</span>
              {q.q}
            </p>
            <div className="space-y-2">
              {q.options.map((opt) => {
                const isSel     = selected[qi] === opt.k;
                const isCorrect = opt.k === q.correct;
                let cls = 'border rounded-md px-3 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2 w-full text-left ';
                if (!submitted) {
                  cls += isSel
                    ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 text-teal-900 dark:text-teal-100'
                    : 'border-slate-200 dark:border-slate-700 hover:border-teal-400 text-slate-700 dark:text-slate-300';
                } else {
                  if (isCorrect)       cls += 'border-green-500 bg-green-50 dark:bg-green-900/20 text-green-900 dark:text-green-100';
                  else if (isSel)      cls += 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-900 dark:text-red-100';
                  else                 cls += 'border-slate-100 dark:border-slate-800 text-slate-400 dark:text-slate-600 opacity-50';
                }
                return (
                  <button
                    key={opt.k}
                    data-testid={`quiz-option-${qi}-${opt.k}`}
                    className={cls}
                    onClick={() => !submitted && setSelected((prev) => ({ ...prev, [qi]: opt.k }))}
                    disabled={submitted}
                  >
                    {submitted && isCorrect  && <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0" />}
                    {submitted && isSel && !isCorrect && <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />}
                    <span className="font-mono text-xs font-bold mr-1">{opt.k.toUpperCase()}.</span>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {submitted && (
              <div className="mt-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-md p-3">
                <p className="text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                  <span className="font-semibold text-teal-700 dark:text-teal-400">Explanation: </span>
                  {q.explanation}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-6">
        {!submitted ? (
          <button
            data-testid="quiz-submit"
            onClick={() => Object.keys(selected).length > 0 && setSubmitted(true)}
            disabled={Object.keys(selected).length === 0}
            className="px-5 py-2 bg-teal-600 hover:bg-teal-500 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-medium rounded-md transition-colors text-sm"
          >
            Submit Answers ({Object.keys(selected).length}/{QUESTIONS.length} answered)
          </button>
        ) : (
          <>
            <div
              data-testid="quiz-score"
              className={`px-4 py-2 rounded-md text-sm font-semibold ${
                score === QUESTIONS.length
                  ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
                  : score >= 3
                  ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-800'
                  : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
              }`}
            >
              Score: {score}/{QUESTIONS.length}
              {score === QUESTIONS.length && ' \u2014 Perfect!'}
              {score >= 3 && score < QUESTIONS.length && ' \u2014 Good work'}
              {score < 3 && ' \u2014 Review the sequence network topology rules above and try again'}
            </div>
            <button
              data-testid="quiz-reset"
              onClick={reset}
              className="text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 underline transition-colors"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </SectionWrapper>
  );
};

// ─── Section 9: Connected Topics & Coming Next ────────────────────────────────
const ConnectedTopics = () => (
  <SectionWrapper number="9" title="Connected Topics" id="connected-topics" alt>
    <p className="text-slate-600 dark:text-slate-400 text-sm mb-5">
      Sequence networks sit at the centre of power systems analysis. M04 gave you the decomposition
      algebra; M05 gives you the circuit models. M06 puts them together: connect the three networks
      at the fault bus and solve for every fault type.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">
          Prerequisites
        </p>
        <div className="space-y-2">
          {[
            {
              to: '/symmetrical-components',
              label: 'M04 Symmetrical Components',
              desc: 'The decomposition algebra: V\u2081, V\u2082, V\u2080 and the a-operator. Sequence networks are the circuit side of M04 math.',
            },
            {
              to: '/transformers',
              label: 'M02 Transformer Systems',
              desc: 'Delta, wye, vector groups. Zero-sequence blocking rules come directly from M02 winding connection analysis.',
            },
            {
              to: '/per-unit',
              label: 'M03 Per-Unit Analysis',
              desc: 'All impedances in all three sequence networks must be on the same MVA/kV base. M03 base conversion is required.',
            },
          ].map(({ to, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg hover:border-teal-400 dark:hover:border-teal-700 transition-colors bg-white dark:bg-slate-900"
            >
              <ArrowLeft className="h-4 w-4 text-slate-400 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 dark:text-slate-500 mb-3">
          Next Steps
        </p>
        <div className="space-y-2">
          {[
            {
              to: '/fault-analysis',
              label: 'M06 Fault Analysis',
              desc: 'Connect Z\u2081, Z\u2082, Z\u2080 at the fault bus. Compute SLG, LL, DLG, 3PH currents, fault MVA, and equipment duty.',
            },
            {
              to: '/protection',
              label: 'M07 Protection & Grounding',
              desc: 'Set overcurrent and ground relays using the sequence currents computed with M05/M06 models.',
            },
          ].map(({ to, label, desc }) => (
            <Link
              key={to}
              to={to}
              className="flex items-center gap-3 p-3 border border-teal-100 dark:border-teal-900/40 rounded-lg hover:border-teal-400 dark:hover:border-teal-700 transition-colors bg-teal-50/50 dark:bg-teal-900/10"
            >
              <ArrowRight className="h-4 w-4 text-teal-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-900 dark:text-white font-heading">{label}</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">{desc}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>

    {/* Coming Next card */}
    <div className="bg-gradient-to-br from-indigo-50 to-teal-50 dark:from-indigo-950/40 dark:to-teal-950/40 border border-indigo-200 dark:border-indigo-800 rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-mono font-semibold text-indigo-700 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 border border-indigo-200 dark:border-indigo-800 px-2 py-0.5 rounded">
          M06
        </span>
        <p className="text-sm font-semibold text-slate-900 dark:text-white font-heading">
          Coming Next: Fault Analysis
        </p>
      </div>
      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
        In this module you built the Thevenin sequence networks — the three per-domain circuit models
        that represent your power system at the fault bus. Module 06 uses those models to compute
        actual fault currents for every standard fault type.{' '}
        <strong className="text-slate-800 dark:text-slate-200">
          Each fault type has a specific rule for how Z\u2081, Z\u2082, Z\u2080 are connected:
        </strong>{' '}
        series (SLG), series Z\u2081+Z\u2082 (LL), series Z\u2081 with parallel Z\u2082\u2225Z\u2080 (DLG),
        or Z\u2081 alone (three-phase).
      </p>
      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed mb-3">
        M06 converts the per-unit sequence currents into physical kA values, calculates fault MVA,
        checks equipment interrupting duty against switchgear ratings, and demonstrates the complete
        short-circuit study workflow used in IEEE 551 (Violet Book) and IEC 60909 compliance
        calculations.
      </p>
      <Link
        to="/fault-analysis"
        data-testid="coming-next-m06-link"
        className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 dark:text-indigo-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
      >
        Go to M06 Fault Analysis
        <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  </SectionWrapper>
);

// ─── Main Module Page ─────────────────────────────────────────────────────────
const SequenceNetworksModule = () => (
  <div data-testid="sequence-networks-module">
    {/* Module Header */}
    <div className="relative bg-gradient-to-br from-[#0B172A] via-[#111840] to-[#1a0d2e] text-white px-4 sm:px-6 lg:px-8 py-12 overflow-hidden">
      {/* Sequence color bars */}
      <div className="absolute bottom-0 left-0 right-0 flex h-1">
        <div className="flex-1" style={{ background: '#0d9488' }} />
        <div className="flex-1" style={{ background: '#ea580c' }} />
        <div className="flex-1" style={{ background: '#7c3aed' }} />
      </div>
      <div className="relative max-w-4xl mx-auto">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-5 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Curriculum Map
        </Link>
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-mono font-semibold text-indigo-400 bg-indigo-400/10 border border-indigo-400/20 px-2 py-1 rounded">
            M05
          </span>
          <span className="text-xs font-bold bg-teal-500 text-white px-2 py-0.5 rounded-full">LIVE</span>
          <span className="text-xs text-slate-400 uppercase tracking-widest">
            Prerequisite: M04 Symmetrical Components
          </span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold font-heading mb-3 leading-tight">
          Sequence Networks
        </h1>
        <p className="text-slate-300 text-lg mb-4">
          Building Positive, Negative, and Zero-Sequence Thevenin Models
        </p>
        <p className="text-slate-400 text-sm max-w-2xl mb-4">
          Construct the three per-domain Thevenin circuit models for any power system. Master
          transformer zero-sequence topology rules, the 3Rn neutral resistor, and the connection
          rules that link sequence networks to fault currents for every fault type.
        </p>
        <div className="flex flex-wrap gap-2">
          <span
            className="text-xs px-2.5 py-1 rounded-full border font-mono"
            style={{ background: '#0d948815', borderColor: '#0d948840', color: '#5eead4' }}
          >
            {'Z\u2081 Positive \u2192 Teal'}
          </span>
          <span
            className="text-xs px-2.5 py-1 rounded-full border font-mono"
            style={{ background: '#ea580c15', borderColor: '#ea580c40', color: '#fb923c' }}
          >
            {'Z\u2082 Negative \u2192 Orange'}
          </span>
          <span
            className="text-xs px-2.5 py-1 rounded-full border font-mono"
            style={{ background: '#7c3aed15', borderColor: '#7c3aed40', color: '#a78bfa' }}
          >
            {'Z\u2080 Zero \u2192 Purple'}
          </span>
        </div>
      </div>
    </div>

    {/* Section Nav */}
    <div className="sticky top-14 z-20 bg-white/95 dark:bg-slate-950/95 backdrop-blur-sm border-b border-slate-200 dark:border-slate-800 overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
        <div className="flex gap-0 py-0" style={{ scrollbarWidth: 'none' }}>
          {[
            { n: 1, label: 'Why',        href: '#why-it-matters' },
            { n: 2, label: 'Builder',    href: '#network-builder' },
            { n: 3, label: 'Theory',     href: '#core-theory' },
            { n: 4, label: 'Formulas',   href: '#formulas' },
            { n: 5, label: 'Mistakes',   href: '#common-mistakes' },
            { n: 6, label: 'Example',    href: '#worked-example' },
            { n: 7, label: 'Calculators',href: '#calculators' },
            { n: 8, label: 'Quiz',       href: '#quiz' },
            { n: 9, label: 'Next',       href: '#connected-topics' },
          ].map(({ n, label, href }) => (
            <a
              key={n}
              href={href}
              className="px-3 py-3 text-xs font-medium text-slate-500 dark:text-slate-500 hover:text-teal-700 dark:hover:text-teal-400 whitespace-nowrap border-b-2 border-transparent hover:border-teal-500 transition-colors flex-shrink-0"
            >
              <span className="text-teal-600 dark:text-teal-500 mr-1 font-mono">{n}.</span>{label}
            </a>
          ))}
        </div>
      </div>
    </div>

    {/* All 9 Sections */}
    <WhyItMatters />
    <NetworkBuilderSection />
    <CoreTheory />
    <FormulaSheet />
    <CommonMistakes />
    <WorkedExample />
    <CalculatorsSection />
    <SelfCheckQuiz />
    <ConnectedTopics />

    {/* Bottom navigation */}
    <div className="border-t border-slate-200 dark:border-slate-800 px-4 sm:px-6 lg:px-8 py-6">
      <div className="max-w-4xl mx-auto flex items-center justify-between">
        <Link
          to="/symmetrical-components"
          data-testid="prev-module-btn"
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          M04 Symmetrical Components
        </Link>
        <Link
          to="/fault-analysis"
          data-testid="next-module-btn"
          className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 hover:text-teal-700 dark:hover:text-teal-400 transition-colors"
        >
          M06 Fault Analysis
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </div>
);

export default SequenceNetworksModule;
